// src/components/ProductsPreview.js
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { AnimatedSection } from './common/AnimatedSection';

/* ---- Animations ---- */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ===== styled ===== */
const ProductsSection = styled.section`
  background: #111111;
  overflow: hidden;
`;

const CenteredContainerForTitle = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  @media (max-width: 900px) { padding: 0 16px; }
`;

const SectionTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 700;
  color: #111;
  text-align: center;
  margin-bottom: 4rem;
  letter-spacing: -1px;
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 80vh;
  min-height: 700px;
  background: #111;
  will-change: transform;
  overflow: hidden;

  /* 스와이프 제스처가 세로 스크롤과 충돌하지 않도록 */
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  @media (max-width: 768px) {
    height: 75vh;
    min-height: 560px;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  height: 100%;
  width: ${p => p.count * 100}%;
  /* 드래그 중에는 transition을 끄고, 평소엔 부드럽게 */
  transition: ${p => (p.isDragging ? 'none' : 'transform 0.8s cubic-bezier(0.4,0,0.2,1)')};
  will-change: transform;
`;

const Slide = styled.div`
  width: ${p => 100 / p.count}%;
  height: 100%;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`;

const BackgroundImage = styled.div`
  position: absolute; inset: 0;
  background-image: url(${p => p.src});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;

  &::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(135deg,
    rgba(0,0,0,0.6) 0%,
    rgba(0,0,0,0.3) 50%,
    rgba(0,0,0,0.65) 100%);
  }
`;

const InfoWrapper = styled.div`
  position: absolute; left: 8%; top: 65%;
  transform: translateY(-50%);
  z-index: 3; max-width: 800px;
  animation: ${fadeInUp} 1s ease-out;

  @media (max-width: 768px) {
    right: 6%;
    left: auto;
    top: 60%;
    transform: translateY(0);
    max-width: 88%;
    padding: 0;
    text-align: right;
  }
`;

const ProductCategory = styled.p`
  color: #b6b6b6ff;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.7);
  font-weight: 700; margin-bottom: 1rem;
  font-size: 1.3rem; text-transform: uppercase; letter-spacing: 1px;
  @media (max-width: 768px) { font-size: 0.95rem; text-align: right; }
`;

const ProductTitle = styled.h3`
  font-size: 6rem; font-weight: 700; line-height: 1.1;
  margin-bottom: 2rem; color: #fff;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.7);
  white-space: nowrap;

  @media (max-width: 1200px) { font-size: 3rem; }
  @media (max-width: 992px)  { font-size: 2.6rem; }
  @media (max-width: 768px)  { font-size: 2.3rem; margin-bottom: 1.2rem; text-align: right; }
`;

const ViewButton = styled.button`
  background: transparent; color: #fff; border: 1px solid #fff; border-radius: 30px;
  padding: 1rem 2.5rem; font-size: 1.1rem; font-weight: 600;
  cursor: pointer; transition: all .3s ease; text-transform: uppercase; letter-spacing: 1px;

  &:hover { background: #fff; color: #111; transform: translateY(-2px); }

  span { margin-left: .5rem; transition: transform .3s ease; }
  &:hover span { transform: translateX(5px); }

  @media (max-width: 768px) {
    bottom: 10%;
    padding: 0.7rem 1.6rem;
    font-size: 0.8rem;
    border-radius: 9999px;
  }
`;

const DescriptionWrapper = styled.div`
  position: absolute; right: 8%; bottom: 15%;
  max-width: 600px; z-index: 3;
  animation: ${fadeInUp} 1s .3s ease-out backwards;

  p {
    font-size: 1.4rem;
    font-weight: 530;
    line-height: 1.8;
    color: rgba(255,255,255,0.9);
    text-align: right;
    text-shadow: 1px 1px 4px rgba(0,0,0,0.7);
  }
`;

const SliderControls = styled.div`
  position: absolute; bottom: 3rem; left: 50%; transform: translateX(-50%);
  display: flex; gap: 1.2rem; z-index: 4;

  @media (max-width: 768px) { bottom: 1.6rem; gap: 0.8rem; }
`;

const SliderDot = styled.button`
  width: 12px; height: 12px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.6);
  background: ${p => (p.active ? '#fff' : 'transparent')};
  cursor: pointer; transition: all .3s ease;

  &:hover { background: #fff; transform: scale(1.2); }

  @media (max-width: 768px) { width: 10px; height: 10px; }
`;

const ArrowButton = styled.button`
  position: absolute; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,0.1);
  border: 2px solid rgba(255,255,255,0.3);
  width: 60px; height: 60px; border-radius: 50%;
  color: #fff; font-size: 1.5rem; cursor: pointer;
  transition: all .3s ease; z-index: 4; backdrop-filter: blur(10px);
  ${p => (p.direction === 'left' ? 'left: 2rem;' : 'right: 2rem;')}

  @media (max-width: 992px) { display: none; }
`;

/* ---- Viewport Hook ---- */
const useIsMobile = (query = '(max-width: 768px)') => {
  const [isMobile, setIsMobile] = useState(() =>
      (typeof window !== 'undefined' ? window.matchMedia(query).matches : false)
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return isMobile;
};

/* ---- Data ---- */
const productSolutions = [
  {
    category: '도로 안전경보시스템',
    title: '캐리어형 비상표시장치',
    description: '신속한 경보와 높은 시인성으로 \n작업자와 운전자의 안전을 지켜드립니다',
    descriptionMobile: '',
    backgroundImage: '/images/products/traffic-safety.webp',
    backgroundImageMobile: '/images/products/traffic-safety-mobile.webp',
    link: '/products/traffic-safety/carrier-emergency-sign'
  },
  {
    category: '도로 안전유도시설',
    title: '디딤 – Safe',
    description: '비상상황 발생 시 안전한 통행과 대피를 돕습니다',
    descriptionMobile:  '',
    backgroundImage: '/images/products/guardrail.webp',
    link: '/products/guardrail/safety-stairs'
  },
  {
    category: '도로환경 개선시설',
    title: '쉼 in 카',
    description: '도로 작업자들의 근무 환경 개선을 위한\n 이동식 복지 공간을 제공합니다',
    descriptionMobile:  '',
    backgroundImage: '/images/products/facilities.webp',
    link: '/products/facilities/shelter-bus'
  },
  {
    category: '도로 안내표지시스템',
    title: '도로표지판',
    description: '도로이용자의 편의와 원활한 교통흐름을 위해 \n도로 표지를 제공합니다',
    descriptionMobile:  '',
    backgroundImage: '/images/products/signage.webp',
    link: '/products/signage/road-sign'
  },
];

const AUTOPLAY_INTERVAL = 5000;
const FIRST_DELAY = 1000;

/* ===== Component ===== */
function ProductsPreview() {
  const [titleRef, titleVisible] = useScrollAnimation();
  const [sliderRef, sliderVisible] = useScrollAnimation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const isMobile = useIsMobile();

  /* ----- autoplay timers ----- */
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const clearTimers = () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (timeoutRef.current)  { clearTimeout(timeoutRef.current);    timeoutRef.current = null; }
  };

  const startAutoplay = (withFirstDelay = true) => {
    clearTimers();
    const run = () => {
      intervalRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % productSolutions.length);
      }, AUTOPLAY_INTERVAL);
    };
    withFirstDelay ? (timeoutRef.current = setTimeout(run, FIRST_DELAY)) : run();
  };

  useEffect(() => {
    if (sliderVisible) {
      setCurrentSlide(0);
      startAutoplay(true);
    } else {
      clearTimers();
    }
    return clearTimers;
  }, [sliderVisible]);

  const nextSlide = () => { setCurrentSlide(p => (p + 1) % productSolutions.length); };
  const prevSlide = () => { setCurrentSlide(p => (p === 0 ? productSolutions.length - 1 : p - 1)); };
  const goTo = (idx) => { setCurrentSlide(idx); };

  /* ----- touch / swipe ----- */
  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const dxRef = useRef(0);
  const [dragPercent, setDragPercent] = useState(0); // wrapper에 더해 줄 이동 %
  const [isDragging, setIsDragging] = useState(false);

  const onTouchStart = (clientX, clientY) => {
    startXRef.current = clientX;
    startYRef.current = clientY;
    dxRef.current = 0;
    setDragPercent(0);
    setIsDragging(false);
    // 제스처 동안 자동재생 잠시 중지
    clearTimers();
  };

  const onTouchMove = (clientX, clientY, e) => {
    const dx = clientX - startXRef.current;
    const dy = clientY - startYRef.current;

    // 가로 스와이프만 인식
    if (!isDragging && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      setIsDragging(true);
    }
    if (!isDragging || !containerRef.current) return;

    e?.preventDefault?.(); // 가로 드래그 시 페이지 스크롤 방지

    dxRef.current = dx;
    const w = containerRef.current.clientWidth || 1;
    // wrapper 기준 % (슬라이드 하나 폭은 100/count%)
    const percent = (dx / w) * (100 / productSolutions.length);
    setDragPercent(percent);
  };

  const onTouchEnd = () => {
    if (!containerRef.current) { setDragPercent(0); startAutoplay(false); return; }

    const w = containerRef.current.clientWidth || 1;
    const dx = dxRef.current;

    const threshold = Math.max(0.1 * w, 60); // 10% 또는 60px
    if (dx <= -threshold) nextSlide();
    else if (dx >= threshold) prevSlide();

    // 원위치/다음/이전으로 애니메이션 복귀
    setDragPercent(0);
    setIsDragging(false);

    // 제스처 종료 뒤 자동재생 재개
    startAutoplay(false);
  };

  /* 줄바꿈 문자열(\n) -> <br/> */
  const renderMultiline = (text) => {
    const parts = String(text || '').split('\n');
    return parts.map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < parts.length - 1 && <br />}
        </React.Fragment>
    ));
  };

  const getDescription = (p) => {
    const text = isMobile
        ? (p.descriptionMobile ?? p.description)
        : (p.descriptionDesktop ?? p.description);
    return renderMultiline(text);
  };

  const basePercent = (currentSlide * (100 / productSolutions.length)).toFixed(6);
  const wrapperStyle = { transform: `translateX(calc(-${basePercent}% + ${dragPercent}%))` };

  return (
      <ProductsSection>
        {/* <CenteredContainerForTitle>
        <AnimatedSection ref={titleRef} isVisible={titleVisible} animation="fadeInUp">
          <SectionTitle>PRODUCTS</SectionTitle>
        </AnimatedSection>
      </CenteredContainerForTitle> */}

        <AnimatedSection ref={sliderRef} isVisible={sliderVisible} animation="fadeInUp">
          <SliderContainer
              ref={containerRef}
              onTouchStart={(e) => onTouchStart(e.touches[0].clientX, e.touches[0].clientY)}
              onTouchMove={(e) => onTouchMove(e.touches[0].clientX, e.touches[0].clientY, e)}
              onTouchEnd={onTouchEnd}
              onMouseDown={(e) => onTouchStart(e.clientX, e.clientY)}
              onMouseMove={(e) => isDragging && onTouchMove(e.clientX, e.clientY, e)}
              onMouseUp={onTouchEnd}
              onMouseLeave={() => isDragging && onTouchEnd()}
          >
            <SlideWrapper
                style={wrapperStyle}
                isDragging={isDragging}
                count={productSolutions.length}
            >
              {productSolutions.map((product, index) => {
                const bgSrc =
                    isMobile && product.backgroundImageMobile
                        ? product.backgroundImageMobile
                        : product.backgroundImage;

                return (
                    <Slide key={index} count={productSolutions.length}>
                      <BackgroundImage src={bgSrc} />
                      <InfoWrapper>
                        <ProductCategory>{product.category}</ProductCategory>
                        <ProductTitle>{product.title}</ProductTitle>
                        <ViewButton onClick={() => (window.location.href = product.link)}>
                          View<span>→</span>
                        </ViewButton>
                      </InfoWrapper>
                      <DescriptionWrapper>
                        <p>{getDescription(product)}</p>
                      </DescriptionWrapper>
                    </Slide>
                );
              })}
            </SlideWrapper>

            <ArrowButton direction="left" onClick={() => { clearTimers(); prevSlide(); startAutoplay(false); }}>‹</ArrowButton>
            <ArrowButton direction="right" onClick={() => { clearTimers(); nextSlide(); startAutoplay(false); }}>›</ArrowButton>

            <SliderControls>
              {productSolutions.map((_, index) => (
                  <SliderDot
                      key={index}
                      active={currentSlide === index}
                      onClick={() => { clearTimers(); goTo(index); startAutoplay(false); }}
                      aria-label={`슬라이드 ${index + 1}로 이동`}
                  />
              ))}
            </SliderControls>
          </SliderContainer>
        </AnimatedSection>
      </ProductsSection>
  );
}

export default ProductsPreview;
